import { Divider, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { formatTimeWithTimezone } from "@src/appV2/redesign/utils/formatTimeWithTimezone";

import { type TimesheetEntry } from "../hooks/useTimesheetEntry";
import { TimesheetTimeEntryRow } from "./TimesheetTimeEntryRow";
import { TimesheetTotalsFooter } from "./TimesheetTotalsFooter";

interface ReadOnlyTimesheetTimeEntriesProps {
  displayTimesheet: TimesheetEntry;
  workplaceTimezone: string;
  formatBreakDisplay: () => string;
  /** Pass the original time when clock-in has been adjusted from the recorded value */
  originalClockInTime?: Date;
  /** Pass the original time when clock-out has been adjusted from the recorded value */
  originalClockOutTime?: Date;
  /** Pass the original formatted break string when break has been adjusted */
  originalBreakDisplay?: string;
  /**
   * Pass the original shift timesheet (hcp_clock_in/out) to show a Verified/Pending
   * hours breakdown below the Total row when the worker is claiming more time than recorded.
   */
  verifiedTimesheet?: TimesheetEntry;
  /** When true, shows "Verified hours" label instead of "Total". Gate behind isMissedPunchRequestEnabledV2. */
  shouldShowVerifiedHoursLabel?: boolean;
}

export function ReadOnlyTimesheetTimeEntries(props: ReadOnlyTimesheetTimeEntriesProps) {
  const {
    displayTimesheet,
    workplaceTimezone,
    formatBreakDisplay,
    originalClockInTime,
    originalClockOutTime,
    originalBreakDisplay,
    verifiedTimesheet,
    shouldShowVerifiedHoursLabel = false,
  } = props;

  function formatWorkplaceTime(date: Date): string {
    return formatTimeWithTimezone(date, workplaceTimezone, false);
  }

  return (
    <Stack spacing={4}>
      <TimesheetTimeEntryRow
        iconType="clock"
        label="Clock in"
        originalValue={
          isDefined(originalClockInTime) ? formatWorkplaceTime(originalClockInTime) : undefined
        }
      >
        <Text bold sx={{ color: (theme) => theme.palette.text.primary }}>
          {formatWorkplaceTime(displayTimesheet.clockInTime)}
        </Text>
      </TimesheetTimeEntryRow>

      <TimesheetTimeEntryRow iconType="break" label="Break" originalValue={originalBreakDisplay}>
        <Text bold sx={{ color: (theme) => theme.palette.text.primary }}>
          {formatBreakDisplay()}
        </Text>
      </TimesheetTimeEntryRow>

      <TimesheetTimeEntryRow
        iconType="clock"
        label="Clock out"
        originalValue={
          isDefined(originalClockOutTime) ? formatWorkplaceTime(originalClockOutTime) : undefined
        }
      >
        <Text bold sx={{ color: (theme) => theme.palette.text.primary }}>
          {formatWorkplaceTime(displayTimesheet.clockOutTime)}
        </Text>
      </TimesheetTimeEntryRow>

      <Divider />

      <TimesheetTotalsFooter
        displayTimesheet={displayTimesheet}
        verifiedTimesheet={verifiedTimesheet}
        shouldShowVerifiedHoursLabel={shouldShowVerifiedHoursLabel}
        formatOptions={{ areLeadingZeroesAllowed: true, omitZeroMinutes: false }}
      />
    </Stack>
  );
}
